import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { colors } from "../styles";
import { CloseOutlined, RetweetOutlined, DeleteOutlined } from '@ant-design/icons';

const Modal = ({ card, closeModal, onDelete, isOwner }) => {

  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [closeModal]);

  const handleCardClick = (e) => {
    e.stopPropagation();
    setIsFlipped(!isFlipped);
  };

  return (
    <ModalOverlay onClick={closeModal}>
      <Controls>
        <ControlButton onClick={closeModal}>
          <CloseOutlined style={{ fontSize: '20px' }} />
        </ControlButton>
        <ControlButton onClick={(e) => {
          e.stopPropagation();
          setIsFlipped(!isFlipped);
        }}>
          <RetweetOutlined style={{ fontSize: '20px' }} />
        </ControlButton>
        {isOwner && (
          <ControlButton onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}>
            <DeleteOutlined style={{ fontSize: '20px' }} />
          </ControlButton>
        )}
      </Controls>
      <ModalContent onClick={handleCardClick}>
        <CardContainer
          initial={{ rotateY: 0 }}
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        >
          <CardFace $isBack={false}>
            <Image src={card.imgSrc} alt={card.title} />
            <Title>{card.title}</Title>
          </CardFace>

          <CardFace $isBack={true}>
            <BackContent>
              <BackTitle>{card.title}</BackTitle>
              <Description>{card.description}</Description>
            </BackContent>
          </CardFace>
        </CardContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const ModalContent = styled.div`
  perspective: 1000px;
  width: 80vw;
  max-width: 800px;
  cursor: pointer;
`;

const CardContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 80vh;
  max-height: 800px;
  transform-style: preserve-3d;
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  transform: ${props => props.$isBack ? 'rotateY(180deg)' : 'rotateY(0)'};
`;

const Image = styled.img`
  width: 100%;
  height: 80%;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 15px;
`;

const Title = styled.h3`
  font-family: 'Homemade Apple', cursive;
  color: #32005f;
  font-size: 2.5rem;
  margin: 20px 0;
  text-align: center;
`;

const BackTitle = styled(Title)`
`;

const Description = styled.p`
  flex: 1;
  overflow-y: auto;
  color: #333;
  font-family: 'Indie Flower', cursive;
  font-size: 1.4em;
  line-height: 1.6;
  text-align: center;
  max-width: 90%;
  margin: 0 auto;
  padding-bottom: 1rem;
  margin-top: 1rem;

  /* Estilizar scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.text}10;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.secondary}40;
    border-radius: 3px;
  }
`;

const BackContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding: 30px;
`;

const Controls = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  z-index: 1100;
`;

const ControlButton = styled.button`
  background: ${colors.white};
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px ${colors.shadow};
  color: ${colors.text};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    background: ${colors.secondary};
    color: ${colors.white};
  }
`;

export { Modal };
